import { useEffect } from 'react';
import { usePatients } from './usePatients';
import { usePatientStore } from '../stores/patient.store';
import type { Patient } from '../types/patient.types';

interface ActivePatientResult {
  patient: Patient | null;
  patients: Patient[];
  isLoading: boolean;
  isError: boolean;
  setActivePatient: (fhirId: string) => void;
}

/**
 * Resolves the patient currently in context.
 *
 * Auto-selects the primary patient (or the first available) when nothing is
 * selected yet, or when the persisted selection is no longer in the list (e.g.
 * a dependent was detached). Everything downstream — appointments, results —
 * keys off `patient.fhirId`.
 */
export function useActivePatient(): ActivePatientResult {
  const { data: patients = [], isLoading, isError } = usePatients();
  const activeFhirId = usePatientStore((s) => s.activePatientFhirId);
  const setActivePatient = usePatientStore((s) => s.setActivePatient);

  const selected = patients.find((p) => p.fhirId === activeFhirId) ?? null;

  useEffect(() => {
    if (patients.length === 0) return;
    const stillValid = patients.some((p) => p.fhirId === activeFhirId);
    if (!stillValid) {
      const fallback = patients.find((p) => p.isPrimary) ?? patients[0]!;
      setActivePatient(fallback.fhirId);
    }
  }, [patients, activeFhirId, setActivePatient]);

  return {
    patient: selected ?? (patients.find((p) => p.isPrimary) ?? patients[0] ?? null),
    patients,
    isLoading,
    isError,
    setActivePatient,
  };
}
